import {
  Card,
  Row,
  Table,
  Col,
  Input,
  message,
  Spin,
  Modal,
  Tooltip,
  Button,
  Tag,
  Select,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { RiAddBoxFill } from "react-icons/ri";
import AddTagModal from "./Add";
import EditTagModal from "./Edit";
import { hasPermission } from "../../utils/HasPermission";
import { deleteTag, getAllTags } from "../../utils/API";
import { FaPen, FaTrashAlt } from "react-icons/fa";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

interface Tag {
  _id: string;
  name: string;
  color: string;
  category: string[];
  isActive: boolean;
}

const TagsPage = () => {
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [dataSource, setDataSource] = useState<Tag[]>([]);
  const [filteredData, setFilteredData] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentTag, setCurrentTag] = useState<Tag | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 🔍 Filter state
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  // Fetch tags
  const getTags = async () => {
    setLoading(true);
    try {
      const response: any = await getAllTags();
      const tagList = response?.data?.tags || response?.tags || [];
      let flatList: Tag[] = [];

      const flattenTags = (tags: any[]) => {
        for (const tag of tags) {
          flatList.push({
            _id: tag._id,
            name: tag.name,
            category: Array.isArray(tag.category)
              ? tag.category
              : [tag.category],
            isActive: tag.isActive ?? true,
            color: tag.color || "#000000",
          });
          if (Array.isArray(tag.tags)) {
            flattenTags(tag.tags);
          }
        }
      };

      if (Array.isArray(tagList)) {
        flattenTags(tagList);
        const sortedData = flatList
          .sort((a, b) => (b._id > a._id ? 1 : -1))
          .map((tag, index) => ({
            ...tag,
            serialNumber: index + 1,
          }));
        setDataSource(sortedData);
        setFilteredData(sortedData);
      } else {
        setDataSource([]);
        setFilteredData([]);
      }
    } catch (error) {
      message.error("Network error. Please try again.");
      setDataSource([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTags();
  }, []);

  // 🔍 Apply both Search + Category filter
  useEffect(() => {
    let filtered = [...dataSource];
    const searchValue = searchText.toLowerCase();

    if (searchText) {
      filtered = filtered.filter(
        (tag) =>
          tag.name?.toLowerCase().includes(searchValue) ||
          tag.category.some((c) => c.toLowerCase().includes(searchValue)),
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter((tag) =>
        tag.category.includes(categoryFilter),
      );
    }

    setFilteredData(filtered);
    setPage(1); // reset page on filter/search
  }, [searchText, categoryFilter, dataSource]);

  const handleEdit = async (record: Tag) => {
    setCurrentTag(record);
    setIsEditModalVisible(true);
  };

  const handleDeleteConfirmation = (id: string) => {
    setDeleteId(id);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const response: any = await deleteTag(deleteId);
      if (response) {
        message.success("Tag deleted successfully");
        setDataSource((prevData) =>
          prevData.filter((item) => item._id !== deleteId),
        );
        setDeleteId(null);
      }
    } catch (error) {
      message.error("Error deleting tag. Please try again.");
    }
  };

  // Collect all unique categories for dropdown filter
  const uniqueCategories = Array.from(
    new Set(dataSource.flatMap((tag) => tag.category)),
  );

  const hexToRGBA = (hex: string, alpha = 0.15) => {
    let r = 0,
      g = 0,
      b = 0;

    if (hex.length === 4) {
      r = parseInt(hex[1] + hex[1], 16);
      g = parseInt(hex[2] + hex[2], 16);
      b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
      r = parseInt(hex[1] + hex[2], 16);
      g = parseInt(hex[3] + hex[4], 16);
      b = parseInt(hex[5] + hex[6], 16);
    }

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const columns = [
    {
      title: "#",
      dataIndex: "key",
      key: "key",
      render: (_: string, __: Tag, index: number) => (
        <span>{(page - 1) * pageSize + index + 1}</span>
      ),
    },
    {
      title: "Tag Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: Tag) => (
        <Tag
          style={{
            backgroundColor: hexToRGBA(record.color || "#000000", 0.15), // light background
            color: record.color, // text stays original color
            borderRadius: "6px",
            borderColor: record.color,
            fontWeight: 600,
            display: "inline-block",
          }}
        >
          {text}
        </Tag>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (categories: string[]) => (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {categories.map((cat: string) => (
            <Tag
              key={cat}
              color="#24a2dcff"
              style={{
                fontWeight: "bold",
                borderRadius: "12px",
                padding: "0 8px",
              }}
            >
              {cat}
            </Tag>
          ))}
        </div>
      ),
    },
    ...(hasPermission("designations", "update") ||
    hasPermission("designations", "delete")
      ? [
          {
            title: "Action",
            key: "action",
            render: (_: any, record: Tag) => (
              <div style={{ display: "flex", gap: "8px" }}>
                {hasPermission("designations", "update") && (
                  <Tooltip title="Edit">
                    <button
                      onClick={() => handleEdit(record)}
                      className="w-7 h-7 rounded-full bg-indigo-500 text-white flex items-center justify-center"
                    >
                      <FaPen size={12} />
                    </button>
                  </Tooltip>
                )}
                {hasPermission("designations", "delete") && (
                  <Tooltip title="Delete">
                    <button
                      onClick={() => handleDeleteConfirmation(record._id)}
                      className="w-7 h-7 rounded-full bg-rose-600 text-white flex items-center justify-center"
                    >
                      <FaTrashAlt size={12} />
                    </button>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  // Slice data for current page
  const paginatedData = filteredData.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  return (
    <div>
      <Row
        className="m-2"
        align="middle"
        justify="space-between"
        gutter={[16, 16]}
      >
        <Col xs={24} sm={8} md={10} lg={14} xl={16}>
          <h2 className="headername">Tags</h2>
        </Col>
        <Col xs={24} sm={12} md={14} lg={10} xl={8}>
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            {/* 🏷️ Category Filter */}
            <Select
              allowClear
              placeholder="Filter by Category"
              value={categoryFilter || undefined}
              onChange={(val) => setCategoryFilter(val || null)}
              style={{ width: 200 }}
              options={uniqueCategories.map((cat) => ({
                value: cat,
                label: cat,
              }))}
            />
            {/* 🔍 Global Search */}
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            {/* ➕ Add Button */}
            <Button
              type="primary"
              style={{ backgroundColor: "#001529 ", color: "white" }}
              onClick={() => setIsModalOpen(true)}
            >
              <RiAddBoxFill />
              Add
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Card className="w-100">
          <Col>
            <Spin spinning={loading}>
              <Table
                columns={columns}
                dataSource={paginatedData}
                rowKey="_id"
                pagination={{
                  current: page,
                  pageSize: pageSize,
                  total: filteredData.length,
                  showSizeChanger: false,
                  onChange: (pageNumber, size) => {
                    setPage(pageNumber);
                    setPageSize(size || 10);
                  },
                }}
              />
            </Spin>
          </Col>
        </Card>
      </Row>

      {/* Delete Confirmation */}
      <Modal
        open={!!deleteId}
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Tag?</p>
      </Modal>
      {/* Edit Modal */}
      {isEditModalVisible && (
        <EditTagModal
          visible={isEditModalVisible}
          tagId={currentTag?._id}
          onClose={() => setIsEditModalVisible(false)}
          onSuccess={() => {
            setIsEditModalVisible(false);
            getTags();
          }}
        />
      )}

      {/* Add Modal */}
      <AddTagModal
        visible={isModalOpen}
        onTaskCreated={() => {
          getTags();
          setIsModalOpen(false);
        }}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export const TAGS_TABLE_COLUMNS = ["name", "category"];

export default TagsPage;
// export default TagsPage
